import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useLockBodyScroll } from "@/hooks/useLockBodyScroll";
import { X, Loader2, CheckCircle2, Sparkles } from "lucide-react";
import { getApiUrl } from "@/utils/api";
import RichHtml from "@/components/ui/RichHtml";

const API = getApiUrl();

const EMPTY_FORM = {
  applicant_type: "individual",
  name: "",
  company_name: "",
  email: "",
  phone: "",
  area_covered: "",
  message: "",
};

export function BecomeSupplierModal({ product, onClose }) {
  useLockBodyScroll(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim() || !form.area_covered.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/supplier-applications`, {
        product_id: product.product_id,
        ...form,
      });
      setSuccessMessage(data.message || "Thanks for applying! We'll review your application and get back to you soon.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit your application. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      data-testid="become-supplier-modal"
    >
      <div
        className="bg-background max-w-xl w-full max-h-[90vh] overflow-y-auto rounded shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h3 className="font-serif text-xl flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            Become a Preferred Supplier
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1 hover:bg-muted rounded"
            aria-label="Close"
            data-testid="become-supplier-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {successMessage ? (
          <div className="p-8 text-center space-y-3" data-testid="become-supplier-success">
            <CheckCircle2 className="h-12 w-12 text-green-600 mx-auto" />
            <h4 className="font-serif text-lg">Application submitted</h4>
            <p className="text-sm text-muted-foreground max-w-sm mx-auto">{successMessage}</p>
            <button
              type="button"
              onClick={onClose}
              className="mt-2 text-sm font-medium px-4 py-2 rounded bg-primary text-primary-foreground hover:opacity-90 transition-colors"
              data-testid="become-supplier-success-close"
            >
              Close
            </button>
          </div>
        ) : (
          <div className="p-5 space-y-5">
            <RichHtml
              className="prose prose-sm max-w-none [&_a]:text-primary [&_a]:underline"
              data-testid="become-supplier-job-description"
              html={product.job_description}
            />

            <form onSubmit={handleSubmit} className="space-y-4 border-t pt-5" data-testid="become-supplier-form">
              <div className="flex gap-2">
                {["individual", "company"].map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => set({ applicant_type: t })}
                    className={`flex-1 text-sm font-medium py-2 rounded border transition-colors capitalize ${
                      form.applicant_type === t
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-white border-border hover:border-primary"
                    }`}
                    data-testid={`become-supplier-type-${t}`}
                  >
                    {t}
                  </button>
                ))}
              </div>

              <div>
                <label className="text-xs font-medium text-muted-foreground">Full name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => set({ name: e.target.value })}
                  required
                  className="mt-1 w-full border border-border rounded px-3 py-2 text-sm"
                  data-testid="become-supplier-name"
                />
              </div>

              {form.applicant_type === "company" && (
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Company name</label>
                  <input
                    type="text"
                    value={form.company_name}
                    onChange={(e) => set({ company_name: e.target.value })}
                    className="mt-1 w-full border border-border rounded px-3 py-2 text-sm"
                    data-testid="become-supplier-company-name"
                  />
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Email *</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => set({ email: e.target.value })}
                    required
                    className="mt-1 w-full border border-border rounded px-3 py-2 text-sm"
                    data-testid="become-supplier-email"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Phone *</label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => set({ phone: e.target.value })}
                    required
                    className="mt-1 w-full border border-border rounded px-3 py-2 text-sm"
                    data-testid="become-supplier-phone"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-medium text-muted-foreground">Area(s) you can cover *</label>
                <input
                  type="text"
                  value={form.area_covered}
                  onChange={(e) => set({ area_covered: e.target.value })}
                  placeholder="e.g. Hertfordshire, Essex"
                  required
                  className="mt-1 w-full border border-border rounded px-3 py-2 text-sm"
                  data-testid="become-supplier-area"
                />
              </div>

              <div>
                <label className="text-xs font-medium text-muted-foreground">Tell us about your experience</label>
                <textarea
                  value={form.message}
                  onChange={(e) => set({ message: e.target.value })}
                  rows={3}
                  className="mt-1 w-full border border-border rounded px-3 py-2 text-sm"
                  data-testid="become-supplier-message"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full text-sm font-medium px-4 py-2.5 rounded bg-primary text-primary-foreground hover:opacity-90 transition-colors inline-flex items-center justify-center gap-2"
                data-testid="become-supplier-submit"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Submit application
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

export default BecomeSupplierModal;
